import { Divider, Flex } from "@chakra-ui/react";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";

import { AiInterviewerFragment, AiInterviewerStatus } from "../../graphql";
import { useFeatureFlagForCurrentOrImpersonatedUser } from "../../graphql/hooks/useFeatureFlag";
import { useBHScreenPermission } from "../../hooks/useBHScreenPermission";
import useCurrentUser from "../../hooks/useCurrentUser";
import { useAgenticIntakeEnabled } from "../AiInterviewerWizard/AgenticIntake/useAgenticIntakeEnabled";
import { AiInterviewerWizardContext } from "../AiInterviewerWizard/AiInterviewerWizardContext";
import { useFlexIntakeEnabled } from "../AiInterviewerWizard/FlexIntake/useFlexIntakeEnabled";
import { WizardEditorHeader } from "../AiInterviewerWizard/WizardEditorHeader";
import AiInterviewerContent from "./AiInterviewerContent";
import { AiInterviewerContext } from "./AiInterviewerContext";
import AiInterviewerHeader from "./AiInterviewerHeader";
import { AiInterviewerFormValues } from "./types";
import useAiInterviewer from "./useAiInterviewer";
import useInterviewerTabs, { TAB_INDICES } from "./useInterviewerTabs";
import { getFormDefaults, processFormData } from "./utils";

interface AiInterviewerProps {
  aiInterviewer: AiInterviewerFragment;
}

const TABS_WITH_WHITE_BACKGROUND: number[] = [
  TAB_INDICES.CANDIDATES as number,
  TAB_INDICES.GUIDE as number,
  TAB_INDICES.SETTINGS as number,
  TAB_INDICES.NOTIFICATIONS as number,
  TAB_INDICES.TEST_INTERVIEW as number,
  TAB_INDICES.INSIGHTS as number,
];

const AiInterviewer: React.FC<AiInterviewerProps> = ({ aiInterviewer }) => {
  const {
    wizardOn,
    setWizardOn,
    registerVersionFormSubmit,
    setAgenticStepIndex,
    setStepperJobTitle,
    setWizardHeaderProps,
  } = useContext(AiInterviewerWizardContext);
  const agenticIntakeEnabled = useAgenticIntakeEnabled();
  const flexIntakeEnabled = useFlexIntakeEnabled();
  const { activeTab } = useInterviewerTabs({
    aiInterviewerId: aiInterviewer.id,
  });
  const isSuggestionsV2FeatureEnabled =
    useFeatureFlagForCurrentOrImpersonatedUser(
      "ai_interviewer:suggestionsV2:frontend"
    );

  useEffect(() => {
    if (
      flexIntakeEnabled &&
      !agenticIntakeEnabled &&
      aiInterviewer.status === AiInterviewerStatus.Draft &&
      !wizardOn
    ) {
      setWizardOn?.(true);
    }
  }, [
    agenticIntakeEnabled,
    flexIntakeEnabled,
    aiInterviewer.status,
    wizardOn,
    setWizardOn,
  ]);

  // Drive the agentic stepper visual state from the active tab
  useEffect(() => {
    if (!agenticIntakeEnabled || !wizardOn) return;
    setStepperJobTitle?.(aiInterviewer.displayName || "");
    if (activeTab === TAB_INDICES.GUIDE) {
      setAgenticStepIndex?.(2, 1); // Guide: Review
    } else if (activeTab === TAB_INDICES.SETTINGS) {
      setAgenticStepIndex?.(3, 0); // Customization: Options
    } else if (activeTab === TAB_INDICES.NOTIFICATIONS) {
      setAgenticStepIndex?.(3, 1); // Customization: Invites
    } else if (activeTab === TAB_INDICES.TEST_INTERVIEW) {
      setAgenticStepIndex?.(3, 2); // Customization: Test interviews
    } else if (activeTab === TAB_INDICES.CANDIDATES) {
      setAgenticStepIndex?.(4); // Done
    }
  }, [
    agenticIntakeEnabled,
    wizardOn,
    activeTab,
    aiInterviewer.displayName,
    setAgenticStepIndex,
    setStepperJobTitle,
  ]);

  const {
    updateInterviewerStatus,
    updateInterviewerPosition,
    addInterviewerVersion,
    unarchiveAiInterviewer,
  } = useAiInterviewer({
    aiInterviewerId: aiInterviewer.id,
  });

  const initialPositionId = aiInterviewer.position?.id;

  const currentUser = useCurrentUser();
  const companyName = currentUser.organization.name;

  const methods = useForm<AiInterviewerFormValues>({
    defaultValues: getFormDefaults(aiInterviewer, companyName),
  });
  const {
    control,
    handleSubmit,
    formState: { isDirty },
    reset,
  } = methods;
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: "questions",
    keyName: "fieldArrayKey",
  });

  const { canView, canEdit, canManage } = useBHScreenPermission(aiInterviewer);
  const isPermissionReadOnly = canView && !canEdit && !canManage;
  const readOnly =
    aiInterviewer.status === AiInterviewerStatus.Archived ||
    isPermissionReadOnly;

  const [saving, setSaving] = useState(false);
  const [pendingAcceptedSuggestionIds, setPendingAcceptedSuggestionIds] =
    useState<Set<string>>(new Set());

  const addPendingAcceptedSuggestionId = useCallback((suggestionId: string) => {
    setPendingAcceptedSuggestionIds((prev) => {
      if (prev.has(suggestionId)) {
        return prev;
      }
      const next = new Set(prev);
      next.add(suggestionId);
      return next;
    });
  }, []);

  const onSaveQuestions = useCallback(
    (callback?: () => void) => {
      handleSubmit(async (data) => {
        if (saving || !isDirty) {
          callback?.();
          return;
        }
        setSaving(true);
        const processedData = processFormData(data);
        try {
          // Update position if it changed (for ATSless orgs)
          const positionChanged = data.positionId !== initialPositionId;
          if (positionChanged) {
            await updateInterviewerPosition.mutation(data.positionId);
          }

          const saveResult = await addInterviewerVersion.mutation(
            processedData,
            Array.from(pendingAcceptedSuggestionIds)
          );
          const savedAiInterviewer =
            saveResult.data?.addAiInterviewerVersion?.aiInterviewer;
          if (savedAiInterviewer) {
            reset(getFormDefaults(savedAiInterviewer, companyName));
          } else {
            reset(processedData);
          }
          setPendingAcceptedSuggestionIds(new Set());
          callback?.();
        } finally {
          setSaving(false);
        }
      })();
    },
    [
      handleSubmit,
      saving,
      addInterviewerVersion,
      updateInterviewerPosition,
      isDirty,
      reset,
      initialPositionId,
      companyName,
      pendingAcceptedSuggestionIds,
    ]
  );

  useEffect(() => {
    if (registerVersionFormSubmit) {
      registerVersionFormSubmit((callback?: () => void) =>
        onSaveQuestions(callback)
      );
    }
  }, [registerVersionFormSubmit, methods, onSaveQuestions]);

  useEffect(() => {
    if (wizardOn && (agenticIntakeEnabled || flexIntakeEnabled)) {
      setWizardHeaderProps?.({
        aiInterviewer,
        saving,
        isDirty,
        isAgentic: agenticIntakeEnabled,
      });
    } else {
      setWizardHeaderProps?.(null);
    }
  }, [
    wizardOn,
    agenticIntakeEnabled,
    flexIntakeEnabled,
    aiInterviewer,
    saving,
    isDirty,
    setWizardHeaderProps,
  ]);

  return (
    <AiInterviewerContext.Provider value={aiInterviewer}>
      <FormProvider {...methods}>
        {wizardOn && !agenticIntakeEnabled && flexIntakeEnabled && (
          <WizardEditorHeader
            aiInterviewer={aiInterviewer}
            saving={saving}
            isDirty={isDirty}
          />
        )}
        {!wizardOn && (
          <>
            <AiInterviewerHeader
              aiInterviewer={aiInterviewer}
              readOnly={readOnly}
              updateStatusMutation={updateInterviewerStatus}
              unarchiveMutation={unarchiveAiInterviewer}
              onSave={() => onSaveQuestions()}
              saving={saving}
              saveDisabled={!isDirty}
            />
            <Divider />
          </>
        )}
        <Flex
          flexDir="column"
          pt={
            isSuggestionsV2FeatureEnabled
              ? "0"
              : wizardOn && agenticIntakeEnabled
              ? "0"
              : "4"
          }
          pb={wizardOn && agenticIntakeEnabled ? "0" : "4"}
          px={wizardOn && agenticIntakeEnabled ? "0" : { base: 4, lg: 8 }}
          flex="1"
          overflow="auto"
          minH="0"
          bg={
            !wizardOn && TABS_WITH_WHITE_BACKGROUND.includes(activeTab)
              ? "white"
              : undefined
          }
        >
          <AiInterviewerContent
            aiInterviewer={aiInterviewer}
            readOnly={readOnly}
            questions={fields}
            appendQuestion={append}
            removeQuestion={remove}
            move={move}
            pendingAcceptedSuggestionIds={pendingAcceptedSuggestionIds}
            onAcceptSuggestion={addPendingAcceptedSuggestionId}
          />
        </Flex>
      </FormProvider>
    </AiInterviewerContext.Provider>
  );
};

export default AiInterviewer;
